import PageLayout from '@ui/templates/page-template/page-layout';
import styled from 'styled-components';
import {Controller} from 'react-hook-form';
import {Label} from '@ui/atoms/label';
import SelectCompanyValues from '../components/select-values';
import {FeedBackType} from '../components/feedback-type';
import FeedBackInput from '../components/feedback';
import {VerticalSpacer} from '@ui/atoms/spacer';
import {observer} from 'mobx-react-lite';
import {MultiOptionsSelectField} from '@ui/molecules/select/multi-options';
import {TextField} from '@ui/molecules/field/textfield';
import {useFetchHook} from '@hooks/query-hook';
import {GoalsSelectField} from '@ui/molecules/select/goal-select-field/goal-select-field';
import {Button} from '@ui/atoms/button';
import {SelectField} from '@ui/molecules/select/selectfield';
import {UserSelectField} from '@ui/molecules/select/user';
import useCreateFeedbackHook from './create-feedback-hook';
import {useNavigate} from 'react-router';
import {useWorkspaceUseCase} from '@hooks/workspace-usecase';

import {useStoreContext} from '@store/store-context';

const FormWrapper = styled.div<{$embedded?: boolean}>`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  .form {
    width: ${({$embedded}) => ($embedded ? '100%' : '40%')};
    max-width: 100%;
    min-width: 0;

    margin-bottom: 30px;

    form {
      width: 100%;
      max-width: 100%;
      min-width: 0;
    }

    form > label:not(:first-child) {
      margin-top: 24px;
    }
    @media (min-width: 1100px) and (max-width: 1320px) {
      width: ${({$embedded}) => ($embedded ? '100%' : '45%')};
    }
    @media (min-width: 768px) and (max-width: 1100px) {
      width: ${({$embedded}) => ($embedded ? '100%' : '70%')};
    }
    @media (max-width: 768px) {
      width: 100%;
    }
  }
`;

const FeedbackRequestContext = styled.div`
  width: 100%;
  min-height: 113px;
  box-sizing: border-box;
  margin-bottom: 24px;
  padding: 16px;
  border: 1px solid ${({theme}) => theme.colors.borderDark};
  border-radius: 10px;
  background: ${({theme}) => theme.colors.backgroundLight};
  color: ${({theme}) => theme.colors.blackTwo};
  font: 400 16px/24px Graphik, sans-serif;
  overflow-wrap: anywhere;

  strong {
    display: block;
    font-weight: 600;
  }

  p {
    margin: 0;
  }
`;
interface CreateFeedbackProps {
  embedded?: boolean;
  feedbackType?: 'feedback' | 'praise';
  requestId?: string | null;
  prefill?: {
    user: string;
    subjectType: string;
    subjectName: string;
    subjectId?: string;
  };
  onSuccess?: () => void | Promise<void>;
}

const CreateFeedback = ({
  embedded,
  feedbackType,
  requestId,
  prefill,
  onSuccess,
}: CreateFeedbackProps) => {
  const navigate = useNavigate();
  const {
    authStore: {auth},
    groupStore: {groups},
    usersStore: {users: allUsers},
    groupTypeStore: {groupType},
  } = useStoreContext();
  const {isPerformanceActive} = useWorkspaceUseCase();

  const {
    handleFormValueChange,
    users,
    isSubmitting,
    errors,
    handleSubmit,
    onSubmit,
    initGoals,
    FeedbackIsPraise,
    selectedUsers,
    id,
    isDisabled,
    control,
    feedbackForOptions,
  } = useCreateFeedbackHook({feedbackType, requestId, prefill, onSuccess});

  const {data: goals = []} = useFetchHook(
    ['feedback-init-goals', groups, groupType],
    () => initGoals(1, 'networkLevel'),
    {
      enabled: isPerformanceActive,
    },
  );

  const feedbackName = FeedbackIsPraise ? 'praise' : 'feedback';
  const isResponse = Boolean(requestId && prefill);
  const responseRecipient = users.find(
    (user) => String(user.value) === prefill?.user,
  );
  const responseRecipientName = responseRecipient?.label?.name || 'This person';
  const praiseRecipientOptions = users.map((user) => ({
    value: user.value,
    label: user.label.name,
  }));

  const content = (
    <FormWrapper $embedded={embedded}>
      <div className="form">
        <form action="" onSubmit={handleSubmit(onSubmit)}>
          {isResponse ? (
            <>
              <Controller control={control} name="users" render={() => <></>} />
              <Controller
                control={control}
                name="feedbackFor"
                render={() => <></>}
              />
              <Controller
                control={control}
                name="subjectId"
                render={() => <></>}
              />
              <FeedbackRequestContext data-testid="feedback-request-context">
                <strong>{responseRecipientName} requested feedback</strong>
                <p>&ldquo;{prefill?.subjectName}&rdquo;</p>
              </FeedbackRequestContext>
            </>
          ) : (
            <>
              <Label>{FeedbackIsPraise ? 'Recipients' : 'Recipient'}</Label>
              <VerticalSpacer size="8px" />

              <Controller
                control={control}
                name="users"
                render={({onBlur, value}) =>
                  FeedbackIsPraise ? (
                    <MultiOptionsSelectField
                      select="people"
                      name="users"
                      performance={true}
                      showCancelIcon={false}
                      noOptionMessage={'No Member Found'}
                      helper={errors.users?.[0]?.message || ''}
                      state={errors.users?.[0] ? 'error' : 'default'}
                      type="dropdownOnly"
                      removeSelectedFromDropdown={false}
                      placeholder="Select member"
                      options={praiseRecipientOptions}
                      defaultValue={praiseRecipientOptions.filter((user) =>
                        value?.includes(String(user.value)),
                      )}
                      onChange={(data: {value: string; label: string}[]) => {
                        handleFormValueChange(
                          data?.map((d) => d.value),
                          'users',
                        );
                      }}
                    />
                  ) : (
                    <UserSelectField
                      onBlur={onBlur}
                      name="users"
                      defaultValue={value?.[0]}
                      disabled={!!id}
                      helper={errors.users?.[0]?.message || ''}
                      state={errors.users?.[0] ? 'error' : 'default'}
                      onChange={(data: {value: string}) => {
                        handleFormValueChange([data.value], 'users');

                        if (data?.value) {
                          const getUser = allUsers.find(
                            (_user) => _user.id === data?.value,
                          );

                          if (getUser?.reviewer?.id === auth.user.id) {
                            handleFormValueChange('private', 'sharing');
                            return;
                          }
                          if (getUser?.reviewer?.id !== auth.user.id) {
                            handleFormValueChange('manager', 'sharing');
                            return;
                          }
                        }
                      }}
                      placeholder="Select member"
                      options={users}
                      fieldNotFoundPlaceHolder={(searchTerm?: string) =>
                        `Oops! Seems there is no ${searchTerm} found in this workspace`
                      }
                    />
                  )
                }
              />

              <Label>Topic</Label>
              <VerticalSpacer size="8px" />
              <Controller
                control={control}
                name="feedbackFor"
                render={({onBlur, value, onChange}) => (
                  <>
                    {isPerformanceActive && (
                      <SelectField
                        onBlur={onBlur}
                        name="feedbackFor"
                        value={value}
                        defaultValue={value}
                        borderRadius={10}
                        excludeSearchBox
                        margin={value === 'goal' || value === 'custom'}
                        selectedValueFontSize="14px"
                        disabled={!!id}
                        helper={errors.feedbackFor?.message || ''}
                        state={errors.feedbackFor ? 'error' : 'default'}
                        onChange={(data: {value: string}) => {
                          onChange(data?.value);
                          handleFormValueChange(null, 'subjectName');
                          handleFormValueChange(null, 'subjectId');
                        }}
                        placeholder={
                          <span style={{fontSize: '14px', lineHeight: '22px'}}>
                            Select
                          </span>
                        }
                        options={feedbackForOptions}
                        fieldNotFoundPlaceHolder={(searchTerm?: string) =>
                          `Oops! Seems there is no ${searchTerm} found`
                        }
                      />
                    )}

                    {value === 'goal' && (
                      <>
                        <VerticalSpacer size="8px" />
                        <Controller
                          control={control}
                          name="subjectId"
                          render={({value: goalId}) => (
                            <GoalsSelectField
                              placeholder="Select goal"
                              placeholderFontSize="14px"
                              defaultValue={goalId}
                              disabled={!!id}
                              options={goals}
                              onChange={(goal: {
                                value: string;
                                label: string;
                              }) => {
                                handleFormValueChange(
                                  goal.label,
                                  'subjectName',
                                );
                                handleFormValueChange(goal.value, 'subjectId');
                              }}
                            />
                          )}
                        />
                      </>
                    )}

                    {value === 'custom' && (
                      <>
                        <VerticalSpacer size="8px" />
                        <Controller
                          control={control}
                          name="subjectName"
                          render={({
                            onBlur: onTopicBlur,
                            onChange: onTopicChange,
                            value: topic,
                          }) => (
                            <TextField
                              onChange={onTopicChange}
                              value={topic}
                              onBlur={onTopicBlur}
                              disabled={!!id}
                              multiple
                              autoResize
                              minTextareaHeight={52}
                              maxLength={120}
                              placeholder="Write a topic or question"
                              placeholderFontSize="14px"
                              inputStyle={{
                                borderRadius: 10,
                                height: '52px',
                                minHeight: '52px',
                                padding: '12px 14px',
                              }}
                            />
                          )}
                        />
                      </>
                    )}
                  </>
                )}
              />
            </>
          )}

          <Controller
            control={control}
            name="subjectName"
            render={({onBlur, value}) => <></>}
          />
          <Controller
            control={control}
            name="impression"
            render={({onBlur, value}) => <></>}
          />
          <>
            <Label>{FeedbackIsPraise ? 'Praise' : 'Feedback'}</Label>
            <VerticalSpacer size="8px" />
            <Controller
              control={control}
              name="feedback"
              render={({onBlur, value}) => (
                <FeedBackInput
                  placeholder={
                    FeedbackIsPraise
                      ? 'Share specific praise.'
                      : 'Share clear, helpful feedback.'
                  }
                  error={errors.feedback?.message || ''}
                  updateFeedbackInput={(value) => {
                    handleFormValueChange(value, 'feedback');
                  }}
                />
              )}
            />

            <VerticalSpacer size="32px" />
            {FeedbackIsPraise &&
              auth.user.workspace?.settings?.companyValues?.values?.length >
                0 && (
                <>
                  <Label>
                    Give praise based on values{' '}
                    <span
                      style={{
                        fontWeight: 400,
                        color: '#5F5F8C',
                      }}>
                      (optional){' '}
                    </span>
                  </Label>
                  <VerticalSpacer size="8px" />

                  <Controller
                    control={control}
                    name="values"
                    render={({onBlur, value}) => (
                      <SelectCompanyValues
                        updateValue={(value) =>
                          handleFormValueChange(value, 'values')
                        }
                      />
                    )}
                  />
                </>
              )}

            <Label>Visibility</Label>
            <VerticalSpacer size="8px" />
            <Controller
              control={control}
              name="sharing"
              render={({onBlur, value}) => (
                <FeedBackType
                  error={errors.sharing?.message || ''}
                  value={value}
                  disabled={FeedbackIsPraise}
                  user={selectedUsers?.[0]}
                  includePublic={FeedbackIsPraise}
                  updateValue={(value) =>
                    handleFormValueChange(value, 'sharing')
                  }
                />
              )}
            />
          </>
          <VerticalSpacer size="16px" />
          <Button
            width="full"
            type="submit"
            data-form-action={true}
            isLoading={isSubmitting}
            disabled={isSubmitting || isDisabled}
            aria-busy={isSubmitting}>
            {FeedbackIsPraise ? 'Post praise' : 'Send feedback'}
          </Button>
        </form>
      </div>
    </FormWrapper>
  );

  if (embedded) return content;

  return (
    <PageLayout
      title={`Write ${feedbackName}`}
      guideUrl="https://peoplebeam.myhcpage.com/en-us/"
      onClick={() => navigate(-1)}>
      {content}
    </PageLayout>
  );
};
export default observer(CreateFeedback);
